import { useEffect, useState } from "react";
import { cn } from "../../lib/utils";

const HEADER_OFFSET_MOBILE = 112;
const HEADER_OFFSET_DESKTOP = 140;

export default function ServiceNav({ services }) {
  const [activeId, setActiveId] = useState(services[0]?.id);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const elements = services
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      {
        rootMargin: "-35% 0px -55% 0px",
        threshold: [0, 0.2, 0.4, 0.6, 0.8, 1],
      }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [services]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const offset =
      typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
        ? HEADER_OFFSET_DESKTOP
        : HEADER_OFFSET_MOBILE;
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  };

  return (
    <div className="sticky top-[60px] z-30 border-b border-shark-200 bg-white/92 shadow-[0_6px_20px_-18px_rgba(0,0,0,0.2)] backdrop-blur lg:top-[72px]">
      <nav
        aria-label="Service sections"
        className="mx-auto flex max-w-[1280px] items-center gap-1.5 overflow-x-auto px-4 py-3 sm:gap-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {services.map((s) => {
          const Icon = s.icon;
          const isActive = activeId === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollTo(s.id)}
              aria-current={isActive ? "true" : undefined}
              className={cn(
                "inline-flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-1.5 text-[11.5px] font-bold uppercase tracking-[0.08em] transition-colors",
                isActive
                  ? "border-complement-500 bg-complement-50 text-complement-800"
                  : "border-shark-200 bg-white text-shark-500 hover:border-complement-300 hover:text-complement-700"
              )}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2.1} />
              <span className="whitespace-nowrap">{s.navLabel || s.title}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
